import React, { useMemo } from "react";
import { useAppSelector } from "../../store/store";
import { useGetMarketingResourcesQuery, useGetMarketingResourcePublicUrlsQuery } from "../../services/api";
import { config } from "../../configs";
import useHasPermission from "../../hooks/useHasPermission";
import { FileType, PurlModule } from "../../utils/documentsModules";
import DynamicIcon from "./DynamicIcon";
import { useResourceClickHandler } from "../../hooks/useResourceClickHandler";
import { tracker } from "../../services/mixpanel";
import MyDocumentTile from "../MyDocumentTile";

interface IAdditionalResources {
  openAnimatedOverview: (url: string) => void;
  openOverlay: (url: string) => void;
  openOverlay1?: (url: React.ReactNode) => void;
}

const DynamicAdditionalResourcesTile = ({
  openAnimatedOverview,
  openOverlay,
  openOverlay1
}: IAdditionalResources) => {
  const { hasPermission } = useHasPermission();
  const solarStudyInvite = useAppSelector((state) => state.login.solarStudyInvite);
  const { data, isLoading } = useGetMarketingResourcesQuery(PurlModule.ADDITIONAL_RESOURCES);

  const handlePdfModal = (name: string, link: string) => {
    tracker.dashboard.overview.pdfModal.onOpen(name);
    openOverlay(link);
  };

  const handleVideoModal = (name: string, link: string) => {
    tracker.dashboard.overview.videoModal.onOpen(name);
    openAnimatedOverview(link);
  };

  const handleDocumentModal = (
    id: string,
    heading: string,
    showSidebar?: boolean,
    message?: string
  ) => {
    tracker.dashboard.documentModal.onOpen(heading);
    openOverlay1?.(
      <MyDocumentTile
        folder={{ id: id }}
        showSidebar={showSidebar}
        heading={heading}
        message={message}
      />
    );
  };

  const { handleResourceClick } = useResourceClickHandler({
    moduleName: PurlModule.ADDITIONAL_RESOURCES,
    handleVideoModal,
    handlePdfModal,
    handleDocumentModal,

  });

  //no need to short on the basis of order, API is giving sorted data
  const sortedResources = data?.data || [];

  const filteredResources = sortedResources.filter((item: IPurlMarketingResource) => {
    if (!item.isActive) return false;
    if (item.key && !hasPermission(item.key as keyof ITilePermissions)) return false;

    if (item.type === FileType.SOLAR_STUDY && !solarStudyInvite) {
      return false;
    }

    return true;
  });

  const iconKeys = useMemo(() => {
    return filteredResources.map((r: IPurlMarketingResource) => r.icon).filter(Boolean) as string[];
  }, [filteredResources]);

  const { data: urlsData, isLoading: isUrlsLoading } = useGetMarketingResourcePublicUrlsQuery(iconKeys, {
    skip: iconKeys.length === 0,
  });

  const urlsMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (urlsData?.data) {
      urlsData.data.forEach((item) => {
        map[item.key] = item.url;
      });
    }
    return map;
  }, [urlsData]);

  return (
    <div
      className={`bg-[#FEFCFB] rounded-xl w-full pt-1 shadow-lg h-[390px] overflow-y-auto`}
    >
      <h2 className="text-[25px] text-black fond-bold p-1 mb-[8px]">
        Additional Resources
      </h2>
      <div className="grid gap-y-3 grid-cols-3 border-t-4 border-[#EFEFF4] p-2 pt-4">
        {isLoading && (
          <div className="col-span-3 text-center text-sm text-gray-500 py-4">
            Loading Additional Resources...
          </div>
        )}

        {!isLoading &&
          filteredResources.map((item: IPurlMarketingResource) => {
            const isSolarStudy = item.type === FileType.SOLAR_STUDY;

            return (
              <div
                key={item._id || item.key}
                className={`flex flex-col items-center justify-center cursor-pointer place-content-center ${isSolarStudy ? "h-[70%]" : ""
                  }`}
                onClick={() => handleResourceClick(item)}
              >
                {item.icon && (
                  <DynamicIcon
                    url={urlsMap[item.icon]}
                    isLoading={isUrlsLoading}
                    className={`w-auto max-w-[75px] ${isSolarStudy ? "h-[60px] my-2" : "h-[50px]"
                      }`}
                    alt={item.title}
                  />
                )}

                <h3
                  className={`text-center text-[13px] uppercase ${isSolarStudy ? "pl-1.5" : "mt-4"
                    }`}
                >
                  {item.title}
                </h3>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default DynamicAdditionalResourcesTile;
